import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Finding, Severity, TrainingExample, CrackAnalysis, InspectionRecord, InspectionMode, GEA_DEFECT_REGISTRY } from '../types';
import { saveEntryToVault, getVaultEntries, deleteVaultEntry } from '../services/vaultService';
import { queryAdvisor } from '../services/claudeService';
import { generateInspectionReport } from '../services/pdfService';
import { getProjectHandle, readProjectRecords, readDiscoveryRecords } from '../services/fileSystemService';
import { LoadingOverlay } from './LoadingOverlay';
import { VaultEntryEditor } from './VaultEntryEditor';

interface TrainingModuleProps {
  onClose: () => void;
  onViewTrajectory?: (updatedFindings: Finding[]) => void;
  initialLabData?: { image: string, findings: Finding[], timeline: CrackAnalysis[], allFindings: Finding[], hasTelemetry?: boolean, customer?: string, asset?: string };
}

type ModuleTab = 'LIBRARY' | 'INSTRUCTOR' | 'LAB' | 'VAULT';

export const TrainingModule: React.FC<TrainingModuleProps> = ({ onClose, onViewTrajectory, initialLabData }) => {
  const [activeTab, setActiveTab] = useState<ModuleTab>(initialLabData ? 'LAB' : 'VAULT');
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [isAsking, setIsAsking] = useState(false);

  // Defect Types State
  const [defectTypes, setDefectTypes] = useState(GEA_DEFECT_REGISTRY);
  const [showCustomTypeForm, setShowCustomTypeForm] = useState(false);
  const [customTypeData, setCustomTypeData] = useState({ title: '', desc: '', icon: '🏷️' });

  // Vault/Folder State
  const [groundTruthEntries, setGroundTruthEntries] = useState<TrainingExample[]>([]);
  const [showImporter, setShowImporter] = useState(false);
  const [inspectionHistory, setInspectionHistory] = useState<InspectionRecord[]>([]);

  // Vault Entry Editor modal — when set, displays the standalone editor overlay.
  const [vaultEditEntry, setVaultEditEntry] = useState<TrainingExample | null>(null);

  // Lab State
  const [labImage, setLabImage] = useState<string | null>(initialLabData?.image || null);
  const [findings, setFindings] = useState<Finding[]>(initialLabData?.findings || []);
  const [missionFindings, setMissionFindings] = useState<(Finding & { frameImage?: string; originalAnalysis?: CrackAnalysis })[]>(initialLabData?.allFindings || []);
  const [currentMissionIdx, setCurrentMissionIdx] = useState<number | null>(initialLabData?.allFindings?.length ? 0 : null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentBox, setCurrentBox] = useState<[number, number, number, number] | null>(null);
  const [selectedFindingIdx, setSelectedFindingIdx] = useState<number | null>(initialLabData ? 0 : null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDraggingOverLab, setIsDraggingOverLab] = useState(false);

  // Geometry & Layout State
  const [imgNaturalSize, setImgNaturalSize] = useState<{ w: number, h: number } | null>(null);
  const [containerSize, setContainerSize] = useState<{ w: number, h: number } | null>(null);

  // Zoom & Pan State
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isMovingBox, setIsMovingBox] = useState(false);
  const [isResizingBox, setIsResizingBox] = useState(false);
  const [grabOffset, setGrabOffset] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);

  // Observe Container Size
  // Depends on activeTab because the Lab container is conditionally rendered — the ref
  // is null on mount when VAULT tab is active. When the user switches to LAB, the
  // container element mounts and we need to re-attach the observer.
  useEffect(() => {
    if (!containerRef.current) return;
    // Set the initial size synchronously so layout computes immediately,
    // don't wait for the first ResizeObserver callback.
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setContainerSize({ w: rect.width, h: rect.height });
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        const h = entry.contentRect.height;
        if (w > 0 && h > 0) setContainerSize({ w, h });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [activeTab]);

  // Load Image Dimensions
  useEffect(() => {
    if (!labImage) {
      setImgNaturalSize(null);
      return;
    }
    const img = new Image();
    img.src = labImage;
    img.onload = () => {
      setImgNaturalSize({ w: img.width, h: img.height });
      // Reset view on new image
      setScale(1);
      setOffset({ x: 0, y: 0 });
    };
  }, [labImage]);

  // Calculate Precise Layout (Fit Contain)
  const layout = useMemo(() => {
    if (!imgNaturalSize || !containerSize) return null;
    const { w: cw, h: ch } = containerSize;
    const { w: iw, h: ih } = imgNaturalSize;

    // Calculate "contain" ratio
    const ratio = Math.min(cw / iw, ch / ih);
    const displayW = iw * ratio;
    const displayH = ih * ratio;

    // Center position (base offset before user pan)
    const baseX = (cw - displayW) / 2;
    const baseY = (ch - displayH) / 2;

    return { displayW, displayH, baseX, baseY, ratio };
  }, [imgNaturalSize, containerSize]);

  // Load Vault Data
  // Ground-truth entries ALWAYS come from the NAS vault (the shared source of
  // truth across machines). Local project mode only affects the History
  // Importer (inspectionHistory), not the ground-truth folder itself.
  useEffect(() => {
    const loadData = async () => {
      try {
        // Vault entries — always from NAS via vaultService (IndexedDB-cached)
        const entries = await getVaultEntries();
        setGroundTruthEntries(entries);

        const isLocal = !!getProjectHandle();

        if (isLocal) {
          // History Importer uses the Evidence folder (01_Evidence_Images)
          // Raw JSONs store CrackAnalysis data at root level with findings[] array
          const evRecords = await readDiscoveryRecords();
          setInspectionHistory(evRecords.map(r => ({
            id: r.id || `ev-${r.timestamp}`,
            projectId: 'local',
            timestamp: r.timestamp || Date.now(),
            // image may be stored as frameImage (from VideoInspector) or image (from LiveScanner)
            imageUrl: r.frameImage || r.image || "",
            analysis: r.analysis || {
              detected: (r.findings?.length ?? 0) > 0,
              severity: r.findings?.[0]?.severity || r.severity || 'LOW',
              mode: r.mode || r.inspectionModes?.[0] || 'GENERAL_NDT',
              description: r.description || `${r.findings?.length ?? 0} anomaly(ies) detected`,
              recommendation: r.recommendation || '',
              confidenceScore: r.confidenceScore || 0.9,
              diagnosticNote: r.diagnosticNote || '',
              findings: r.findings || []
            },
            videoName: r.videoName || r.video || "Evidence Frame"
          })));
        } else {
          const historyRaw = localStorage.getItem('staincheck_history');
          if (historyRaw) setInspectionHistory(JSON.parse(historyRaw));
        }

        const savedCustomTypes = localStorage.getItem('sst_custom_defect_types');
        if (savedCustomTypes) {
          setDefectTypes([...GEA_DEFECT_REGISTRY, ...JSON.parse(savedCustomTypes)]);
        }
      } catch (e) {
        console.error("Data access error", e);
      }
    };
    loadData();
  }, [activeTab]);

  const handleAddCustomType = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customTypeData.title.trim()) return;

    const newType = {
      title: customTypeData.title,
      type: customTypeData.title.replace(/\s+/g, '_'),
      desc: customTypeData.desc,
      icon: customTypeData.icon,
      color: "text-[#0000a9]",
      hex: "#0000a9"
    };

    const updatedTypes = [...defectTypes, newType];
    setDefectTypes(updatedTypes);

    // Persist custom types
    const customOnly = updatedTypes.filter(t => !GEA_DEFECT_REGISTRY.some(init => init.title === t.title));
    localStorage.setItem('sst_custom_defect_types', JSON.stringify(customOnly));

    setShowCustomTypeForm(false);
    setCustomTypeData({ title: '', desc: '', icon: '🏷️' });
  };

  const navigateToMissionFinding = useCallback((idx: number) => {
    const f = missionFindings[idx];
    if (!f) return;

    setCurrentMissionIdx(idx);
    if (f.frameImage) setLabImage(f.frameImage);

    const rawFindings = f.originalAnalysis ? [...f.originalAnalysis.findings] : [f];

    // If findings carry labNumber (loaded from verified JSON), restore the original
    // lab order. Otherwise fall back to the natural order.
    const frameFindings = rawFindings.every(ff => ff.labNumber != null)
      ? [...rawFindings].sort((a, b) => (a.labNumber ?? 0) - (b.labNumber ?? 0))
      : rawFindings;

    setFindings(frameFindings);

    // Match by box coordinates exactly
    const matchIdx = frameFindings.findIndex(ff =>
      ff.box.every((val, i) => val === f.box[i]) && ff.type === f.type
    );
    setSelectedFindingIdx(matchIdx !== -1 ? matchIdx : 0);
  }, [missionFindings]);

  const handleLabDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOverLab(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setLabImage(base64);
        setFindings([]);
        setSelectedFindingIdx(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const askInstructor = async () => {
    if (!query.trim()) return;
    setIsAsking(true);
    setResponse("");
    try {
      const res = await queryAdvisor(query);
      setResponse(res);
    } catch (err: any) {
      setResponse(`Technical link failure: ${err.message || 'Unknown Error'}`);
    } finally {
      setIsAsking(false);
    }
  };

  const getTransformedCoords = useCallback((clientX: number, clientY: number) => {
    if (!containerRef.current || !layout) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();

    // Mouse relative to container top-left
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;

    // The image div's current top-left (taking into account base centering + user pan)
    // We treat transformOrigin as effectively top-left for calculation simplicity with explicit offsets
    const currentImageX = layout.baseX + offset.x;
    const currentImageY = layout.baseY + offset.y;

    // Mouse relative to the scaled image top-left
    const relX = mouseX - currentImageX;
    const relY = mouseY - currentImageY;

    // Normalize to 0-1000 based on scaled dimensions
    const x = (relX / (layout.displayW * scale)) * 1000;
    const y = (relY / (layout.displayH * scale)) * 1000;

    return { x, y };
  }, [scale, offset, layout]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!labImage || !layout) return;
    if (e.altKey || e.button === 1) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
      return;
    }
    const coords = getTransformedCoords(e.clientX, e.clientY);

    if (selectedFindingIdx !== null && findings[selectedFindingIdx]) {
      const f = findings[selectedFindingIdx];
      const margin = 30 / scale;
      const [ymin, xmin, ymax, xmax] = f.box;
      const isBottomRight = Math.abs(coords.x - xmax) < margin && Math.abs(coords.y - ymax) < margin;
      if (isBottomRight) { setIsResizingBox(true); return; }
      if (coords.x > xmin && coords.x < xmax && coords.y > ymin && coords.y < ymax) {
        setIsMovingBox(true);
        setGrabOffset({ x: coords.x - xmin, y: coords.y - ymin });
        return;
      }
    }
    setStartPos(coords);
    setIsDrawing(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!layout) return;
    const coords = getTransformedCoords(e.clientX, e.clientY);

    if (isPanning) {
      setOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
      return;
    }
    if (isMovingBox && selectedFindingIdx !== null) {
      const f = findings[selectedFindingIdx];
      const w = f.box[3] - f.box[1];
      const h = f.box[2] - f.box[0];
      const nextBox: [number, number, number, number] = [coords.y - grabOffset.y, coords.x - grabOffset.x, coords.y - grabOffset.y + h, coords.x - grabOffset.x + w];
      updateFinding(selectedFindingIdx, { box: nextBox });
      return;
    }
    if (isResizingBox && selectedFindingIdx !== null) {
      const f = findings[selectedFindingIdx];
      const nextBox: [number, number, number, number] = [f.box[0], f.box[1], Math.max(f.box[0] + 10, coords.y), Math.max(f.box[1] + 10, coords.x)];
      updateFinding(selectedFindingIdx, { box: nextBox });
      return;
    }
    if (isDrawing) {
      setCurrentBox([Math.min(startPos.y, coords.y), Math.min(startPos.x, coords.x), Math.max(startPos.y, coords.y), Math.max(startPos.x, coords.x)]);
    }
  };

  const handleMouseUp = () => {
    if (isDrawing && currentBox) {
      // Validate box size
      const h = currentBox[2] - currentBox[0];
      const w = currentBox[3] - currentBox[1];
      if (h > 10 && w > 10) {
        const newFinding: Finding = {
          type: "Crack",
          severity: Severity.CRITICAL,
          description: "Manual identification.",
          recommendation: "Verify structural integrity.",
          prevention: "N/A",
          box: currentBox,
          isVerified: true // Hand-drawn findings are verified by default
        };
        const next = [...findings, newFinding];
        setFindings(next);
        setSelectedFindingIdx(next.length - 1);

        // SYNC TO MISSION: Add this new manual finding to the missionFindings list so it shows in 3D/Reports
        if (currentMissionIdx !== null && missionFindings[currentMissionIdx]) {
          const currentFrameIdx = currentMissionIdx;
          const mfBase = missionFindings[currentFrameIdx];
          const newMf = {
            ...newFinding,
            frameImage: labImage || undefined,
            originalAnalysis: mfBase.originalAnalysis,
            timestamp: mfBase.timestamp
          };
          setMissionFindings(prev => {
            const updated = [...prev];
            // We append it or insert it nearby... appending is safest for now
            updated.push(newMf);
            return updated;
          });
        }
      }
    }
    setIsDrawing(false); setIsPanning(false); setIsMovingBox(false); setIsResizingBox(false); setCurrentBox(null);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (!labImage || !containerRef.current || !layout) return;
    e.preventDefault();

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Current Image Pos
    const curX = layout.baseX + offset.x;
    const curY = layout.baseY + offset.y;

    // Mouse relative to image top-left (scaled)
    const relX = mouseX - curX;
    const relY = mouseY - curY;

    const factor = Math.pow(1.1, -e.deltaY / 150);
    const newScale = Math.min(Math.max(scale * factor, 0.5), 15);

    // Adjust offset to keep mouse over same image pixel
    // newRelX = relX * (newScale / scale)
    // newOffset = mouseX - layout.baseX - newRelX

    const newRelX = relX * (newScale / scale);
    const newRelY = relY * (newScale / scale);

    const newOffsetX = mouseX - layout.baseX - newRelX;
    const newOffsetY = mouseY - layout.baseY - newRelY;

    setOffset({ x: newOffsetX, y: newOffsetY });
    setScale(newScale);
  };

  const updateFinding = (idx: number, updates: Partial<Finding>) => {
    const next = [...findings];
    const initialBox = next[idx].box;
    const updated = { ...next[idx], ...updates };
    next[idx] = updated;
    setFindings(next);

    // Sync back to mission metadata so edits persist across navigation
    if (currentMissionIdx !== null && missionFindings[currentMissionIdx]) {
      const nextMission = [...missionFindings];
      const mf = nextMission[currentMissionIdx];

      if (mf.originalAnalysis) {
        // Find finding in the original analysis finding list by coordinates
        const fIdx = mf.originalAnalysis.findings.findIndex(f =>
          f.box.every((val, i) => val === initialBox[i])
        );
        if (fIdx !== -1) {
          mf.originalAnalysis.findings[fIdx] = updated;

          // CRITICAL FIX: Update ALL occurrences of this finding on this frame in missionFindings
          // (This ensures that if multiple mission entries point to the same frame finding, they stay in sync)
          for (let i = 0; i < nextMission.length; i++) {
            const item = nextMission[i];
            if (item.originalAnalysis === mf.originalAnalysis && item.box.every((v, j) => v === initialBox[j])) {
              nextMission[i] = { ...item, ...updated };
            }
          }
          setMissionFindings(nextMission);
        }
      } else {
        // Manual finding (no originalAnalysis)
        // Match by box in the missionFindings array directly
        const mfIdx = nextMission.findIndex(m =>
          m.box.every((v, i) => v === initialBox[i]) && m.frameImage === labImage
        );
        if (mfIdx !== -1) {
          nextMission[mfIdx] = { ...nextMission[mfIdx], ...updated };
          setMissionFindings(nextMission);
        }
      }
    }
  };

  const removeFinding = (idx: number) => {
    const target = findings[idx];
    if (!target) return;

    // Remove from local frame findings
    setFindings(findings.filter((_, i) => i !== idx));

    // Remove from mission-wide findings
    setMissionFindings(prev => prev.filter(mf =>
      !(mf.box.every((v, i) => v === target.box[i]) && mf.type === target.type)
    ));

    // Note: caller decides what selectedFindingIdx becomes next (stay on frame, advance to
    // next mission finding, or null). We don't force null here anymore.
  };

  const commitToVault = async (image: string, findingsList: Finding[], meta?: any): Promise<boolean> => {
    // Stamp each finding with its lab number (EV_V6.0_1, EV_V6.0_2, etc.)
    // so the verified JSON files match the numbering shown in the Laboratory UI.
    const numberedFindings = findingsList.map((f, i) => ({
      ...f,
      labNumber: i + 1,
      labId: `EV_V6.0_${i + 1}`
    }));

    const example: TrainingExample = {
      image,
      timestamp: Date.now(),
      analysis: {
        detected: true,
        severity: numberedFindings.reduce((max, f) => {
          const ranks = { [Severity.NONE]: 0, [Severity.LOW]: 1, [Severity.MEDIUM]: 2, [Severity.HIGH]: 3, [Severity.CRITICAL]: 4 };
          return ranks[f.severity] > ranks[max] ? f.severity : max;
        }, Severity.NONE),
        description: meta?.description || `Verified Baseline: ${numberedFindings.map((f, i) => `[${i + 1}] ${f.type}`).join(', ')}.`,
        recommendation: numberedFindings[0]?.recommendation || "N/A",
        confidenceScore: 1.0,
        findings: numberedFindings,
        mode: numberedFindings[0]?.type as any,
        diagnosticNote: "Verified baseline from training module V6.0."
      }
    };

    try {
      await saveEntryToVault(example);
      const updated = await getVaultEntries();
      setGroundTruthEntries(updated);
      return true;
    } catch (e) {
      console.error("Critical Vault Error (IndexedDB)", e);
      return false;
    }
  };

  const saveToVault = async (redirect: boolean = true) => {
    if (!labImage || findings.length === 0) return;
    setIsSaving(true);
    const success = await commitToVault(labImage, findings);
    setIsSaving(false);

    if (success) {
      if (redirect) {
        setTimeout(() => {
          setActiveTab('VAULT');
          alert("Baseline committed to Ground Truth Vault (IndexedDB).");
        }, 500);
      }
    } else {
      alert("Vault Write Failure. Please check browser permissions.");
    }
  };

  const importFromHistory = async (record: InspectionRecord) => {
    const success = await commitToVault(record.imageUrl, record.analysis.findings || [], { description: record.analysis.description });
    if (success) {
      alert("Run data successfully imported into Ground Truth Folder.");
    } else {
      alert("Import failed. Database Error.");
    }
  };

  const deleteFromVault = async (timestamp: number) => {
    if (!window.confirm("Remove this verified entry from ground truth?")) return;
    try {
      await deleteVaultEntry(timestamp);
      const updated = await getVaultEntries();
      setGroundTruthEntries(updated);
    } catch (e) {
      console.error("Deletion failed", e);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-300 animate-fadeIn text-left">
      <header className="p-8 bg-slate-500 border-b border-slate-600 flex justify-between items-center shadow-xl">
        <div className="flex items-center gap-8">
          <div className="flex flex-col">
            <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">GEA | Laboratory Workbench</h2>
            <p className="text-[10px] text-white font-black uppercase tracking-[0.3em] mt-1 flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-400 animate-pulse"></span>
              REFINEMENT LAB | THE SOURCE OF TRUTH
            </p>
          </div>
          <nav className="flex gap-2 bg-slate-900/10 p-1.5 rounded-none border border-slate-600/30">
            {[
              { id: 'VAULT', label: 'GROUND TRUTH FOLDER' },
              { id: 'LAB', label: 'NEURAL LAB' },
              { id: 'INSTRUCTOR', label: 'ADVISOR' },
              { id: 'LIBRARY', label: 'REFERENCE' }
            ].map(t => (
              <button key={t.id} onClick={() => { setActiveTab(t.id as any); setShowImporter(false); }} className={`px-6 py-2.5 rounded-none text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === t.id ? 'bg-[#0000a9] text-white shadow-lg' : 'text-slate-900/60 hover:text-slate-900 hover:bg-slate-400/50'}`}>{t.label}</button>
            ))}
          </nav>
        </div>
        <button onClick={onClose} className="p-3 text-white/70 hover:text-white transition bg-slate-300/10 rounded-none border border-white/10"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg></button>
      </header>

      <main className="flex-1 overflow-hidden p-8 max-w-[1700px] mx-auto w-full">
        {activeTab === 'VAULT' && (
          <div className="h-full flex flex-col space-y-6">
            <div className="flex justify-between items-end border-b border-slate-400 pb-6">
              <div>
                <h3 className="text-xl font-black text-slate-900 uppercase italic">Facility Ground Truth Folder</h3>
                <p className="text-[10px] text-slate-800 font-mono uppercase tracking-[0.2em] mt-2">Verified anchor entries for high-fidelity detection.</p>
              </div>
              <div className="flex items-center gap-4">
                <button onClick={() => setShowImporter(!showImporter)} className={`px-6 py-2.5 rounded-none text-[10px] font-black uppercase tracking-widest transition-all border ${showImporter ? 'bg-amber-600 border-amber-500 text-slate-950' : 'bg-slate-300/5 border-white/10 text-white hover:bg-slate-300/10'}`}>
                  {showImporter ? 'Close Facility Logs' : 'Import from Facility Logs'}
                </button>
                <span className="text-[10px] bg-[#0000a9]/10 text-[#0000a9] border border-[#0000a9]/20 px-4 py-2 rounded-none font-black uppercase">{groundTruthEntries.length} LOGS IN VAULT</span>
              </div>
            </div>

            <div className="flex-1 overflow-hidden flex gap-8">
              <div className="flex-1 overflow-y-auto custom-scrollbar pr-4">
                {groundTruthEntries.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {groundTruthEntries.map((entry, i) => (
                      <div key={entry.timestamp} className="bg-slate-400 border border-slate-500 rounded-none overflow-hidden flex flex-col group hover:border-[#0000a9] transition-all shadow-sm">
                        <div className="aspect-video relative overflow-hidden bg-black">
                          <img src={entry.image || entry.imageUrl || ''} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" alt="Verified Anomaly" />
                          {/* Box overlays per verified finding — uses [ymin,xmin,ymax,xmax] normalized 0–1000 like PDF report */}
                          {entry.analysis?.findings && entry.analysis.findings.length > 0 && (
                            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1000 1000" preserveAspectRatio="none">
                              {entry.analysis.findings.map((f, fi) => {
                                if (!f.box || f.box.length < 4) return null;
                                const [ymin, xmin, ymax, xmax] = f.box;
                                const reg = GEA_DEFECT_REGISTRY.find(d => d.type === f.type || d.title === f.type);
                                const hex = reg?.hex || '#0000a9';
                                const label = String(fi + 1);
                                // Badge sized by label length — fits 1, 12, 123 etc.
                                const badgeW = 38 + (label.length - 1) * 18;
                                const badgeH = 44;
                                // If box too close to top, flip badge inside the box
                                const badgeY = ymin >= badgeH ? ymin - badgeH : ymin;
                                return (
                                  <g key={fi}>
                                    <rect
                                      x={xmin} y={ymin}
                                      width={Math.max(0, xmax - xmin)}
                                      height={Math.max(0, ymax - ymin)}
                                      fill="none"
                                      stroke={hex}
                                      strokeWidth={6}
                                      vectorEffect="non-scaling-stroke"
                                    />
                                    <rect
                                      x={xmin} y={badgeY}
                                      width={badgeW} height={badgeH}
                                      fill={hex}
                                    />
                                    <text
                                      x={xmin + badgeW / 2}
                                      y={badgeY + badgeH / 2}
                                      fill="#ffffff"
                                      fontSize={30}
                                      fontWeight={900}
                                      fontFamily="'JetBrains Mono', monospace"
                                      textAnchor="middle"
                                      dominantBaseline="central"
                                    >{label}</text>
                                  </g>
                                );
                              })}
                            </svg>
                          )}
                          <div className="absolute top-4 left-4 bg-emerald-500 text-slate-950 px-3 py-1 rounded-none text-[8px] font-black uppercase tracking-widest flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-none bg-slate-300 animate-pulse"></div>
                            Human Verified
                          </div>
                        </div>
                        <div className="p-6 flex-1 flex flex-col">
                          <div className="flex justify-between items-start mb-4">
                            <h4 className="text-sm font-black text-slate-900 uppercase">{entry.analysis.findings?.[0]?.type || 'Defect Log'}</h4>
                            <div className="text-right">
                              <div className="text-[9px] font-mono text-slate-800">{new Date(entry.timestamp).toLocaleDateString()}</div>
                              <div className="text-[8px] font-mono text-slate-600">ID {entry.timestamp}</div>
                            </div>
                          </div>
                          <p className="text-[11px] text-slate-800 italic mb-6 line-clamp-2">"{entry.analysis.description}"</p>
                          <div className="mt-auto pt-4 border-t border-slate-400 flex justify-between items-center">
                            <button onClick={() => setVaultEditEntry(entry)} className="text-[10px] font-black text-[#0000a9] uppercase tracking-widest hover:text-white transition">Re-edit Baseline</button>
                            <button onClick={() => deleteFromVault(entry.timestamp)} className="text-[10px] font-black text-red-500/60 uppercase tracking-widest hover:text-red-500 transition">Evict from Vault</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center p-20 opacity-30 italic">
                    <div className="w-24 h-24 bg-slate-300 rounded-none flex items-center justify-center text-slate-300 mb-8 border-2 border-dashed border-slate-400">
                      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                    </div>
                    <h5 className="text-slate-900 font-black uppercase text-sm tracking-widest mb-2">Folder Empty</h5>
                    <p className="text-[11px] font-bold uppercase tracking-widest">No verified ground truth entries in this facility vault.</p>
                  </div>
                )}
              </div>

              {/* History Importer Sidebar */}
              {showImporter && (
                <div className="w-[450px] bg-slate-300 border border-slate-400 rounded-none flex flex-col overflow-hidden animate-slideLeft shadow-xl">
                  <div className="p-8 border-b border-slate-400 bg-slate-300">
                    <h4 className="text-slate-900 font-black uppercase tracking-widest text-sm italic">Facility Log Explorer</h4>
                    <p className="text-[9px] text-slate-800 font-mono mt-1 uppercase tracking-widest">Select previous runs to commit as Ground Truth</p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
                    {inspectionHistory.length > 0 ? inspectionHistory.map((record) => (
                      <div key={record.id} className="bg-slate-300 border border-slate-400 rounded-none p-5 flex flex-col gap-4 group hover:border-[#0000a9]/30 transition-all shadow-sm">
                        <div className="flex gap-4 items-start">
                          <div className="w-20 h-16 bg-slate-900 rounded-none overflow-hidden border border-slate-400">
                            <img src={record.imageUrl} className="w-full h-full object-cover opacity-80" alt="Run Log" />
                          </div>
                          <div className="flex-1 text-left">
                            <h5 className="text-slate-900 font-black uppercase text-[10px] tracking-tight truncate">{record.videoName || 'Generic Run Log'}</h5>
                            <p className="text-[9px] text-slate-800 font-mono mt-1">{new Date(record.timestamp).toLocaleString()}</p>
                            <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded mt-2 inline-block border ${record.analysis.detected ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}>
                              {record.analysis.severity}
                            </span>
                          </div>
                        </div>
                        <button onClick={() => importFromHistory(record)} className="w-full py-2.5 bg-[#0000a9]/10 hover:bg-[#0000a9] text-[#0000a9] hover:text-white border border-[#0000a9]/20 rounded-none text-[9px] font-black uppercase tracking-widest transition-all">
                          Add to Ground Truth
                        </button>
                      </div>
                    )) : (
                      <p className="text-center p-12 text-[10px] text-slate-700 uppercase font-black italic">No historical facility logs found.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'LAB' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 h-full">
            <div className="lg:col-span-8 flex flex-col space-y-4">
              <div className="flex items-center justify-between bg-slate-400 p-5 rounded-none border border-slate-500 shadow-sm">
                <div className="flex items-center gap-6">
                  <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest italic">V6.0 Laboratory Workbench</h4>
                  {missionFindings.length > 0 && (
                    <div className="flex items-center gap-3 border-l border-slate-600 pl-6">
                      <div className="flex flex-col">
                        <span className="text-[8px] text-slate-600 uppercase tracking-widest font-black">Total Anomalies</span>
                        <span className="text-[15px] font-black text-slate-900 leading-none">{missionFindings.length}</span>
                      </div>
                      <div className="w-px h-8 bg-slate-500" />
                      <div className="flex flex-col">
                        <span className="text-[8px] text-emerald-700 uppercase tracking-widest font-black">OK&apos;d</span>
                        <span className="text-[15px] font-black text-emerald-700 leading-none">{missionFindings.filter(f => f.isVerified).length}</span>
                      </div>
                      <div className="w-px h-8 bg-slate-500" />
                      <div className="flex flex-col">
                        <span className="text-[8px] text-amber-700 uppercase tracking-widest font-black">Pending</span>
                        <span className="text-[15px] font-black text-amber-700 leading-none">{missionFindings.filter(f => !f.isVerified).length}</span>
                      </div>
                    </div>
                  )}
                </div>
                {findings.length > 0 && (
                  <button onClick={() => saveToVault()} disabled={isSaving} className="bg-[#0000a9] hover:bg-[#0000c0] text-white px-8 py-3 rounded-none text-[10px] font-black uppercase tracking-widest shadow-xl">COMMIT TO VAULT FOLDER</button>
                )}
              </div>

              <div
                ref={containerRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onWheel={handleWheel}
                onDragOver={(e) => { e.preventDefault(); setIsDraggingOverLab(true); }}
                onDragLeave={() => setIsDraggingOverLab(false)}
                onDrop={handleLabDrop}
                className={`flex-1 bg-slate-300 rounded-none border-2 relative overflow-hidden shadow-2xl transition-all duration-300 ${isPanning ? 'cursor-grabbing' : 'cursor-crosshair'} ${isDraggingOverLab ? 'border-[#0000a9] bg-[#0000a9]/10 scale-[1.01]' : 'border-dashed border-slate-400 hover:border-slate-500'}`}
              >
                {labImage && layout ? (
                  <div
                    className="relative"
                    style={{
                      width: layout.displayW,
                      height: layout.displayH,
                      left: layout.baseX + offset.x,
                      top: layout.baseY + offset.y,
                      transform: `scale(${scale})`,
                      transformOrigin: '0 0'
                    }}
                  >
                    <img src={labImage} className="w-full h-full pointer-events-none select-none" style={{ display: 'block', objectFit: 'fill' }} draggable={false} />

                    {findings.map((f, idx) => {
                      const isActive = selectedFindingIdx === idx;
                      const registryEntry = defectTypes.find(d => d.type === f.type || d.title === f.type);
                      const catColor = registryEntry?.hex || '#64748b';
                      const severityColor = {
                        [Severity.NONE]: '#64748b',
                        [Severity.LOW]: '#3b82f6',
                        [Severity.MEDIUM]: '#eab308',
                        [Severity.HIGH]: '#f97316',
                        [Severity.CRITICAL]: '#ef4444',
                      }[f.severity] || '#64748b';

                      return (
                        <div
                          key={idx}
                          onClick={(e) => { e.stopPropagation(); setSelectedFindingIdx(idx); }}
                          className={`absolute border-2 transition-all cursor-pointer ${isActive ? 'shadow-2xl scale-[1.02] z-20' : 'opacity-80 hover:opacity-100 z-10'}`}
                          style={{
                            top: `${f.box[0] / 10}%`,
                            left: `${f.box[1] / 10}%`,
                            width: `${(f.box[3] - f.box[1]) / 10}%`,
                            height: `${(f.box[2] - f.box[0]) / 10}%`,
                            borderColor: catColor,
                            backgroundColor: `${catColor}15`,
                            boxShadow: isActive ? `0 0 30px ${severityColor}40, inset 0 0 20px ${severityColor}30` : 'none'
                          }}
                        >
                          {/* Inner criticality indicator */}
                          <div
                            className="absolute inset-1 border border-dashed opacity-40 pointer-events-none"
                            style={{ borderColor: severityColor }}
                          />

                          <div className={`absolute top-0 left-0 -translate-y-full px-1.5 py-0.5 text-[7px] font-black uppercase flex items-center gap-1 shadow-md`} style={{ backgroundColor: catColor, color: '#fff' }}>
                            <span className="opacity-70">{f.labNumber ?? idx + 1}</span> <span>{registryEntry?.title || f.type}</span>
                          </div>

                          {isActive && (
                            <div
                              className="absolute -bottom-1 -right-1 w-4 h-4 cursor-nwse-resize border-2 border-white shadow-lg"
                              style={{ backgroundColor: severityColor }}
                            />
                          )}
                        </div>
                      );
                    })}
                    {currentBox && <div className="absolute border-2 border-dashed border-[#0000a9]/60 bg-[#0000a9]/5 pointer-events-none" style={{ top: `${currentBox[0] / 10}%`, left: `${currentBox[1] / 10}%`, width: `${(currentBox[3] - currentBox[1]) / 10}%`, height: `${(currentBox[2] - currentBox[0]) / 10}%` }} />}
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center p-12 text-center text-slate-700 italic">
                    <div className="w-20 h-20 border-2 border-dashed border-slate-800 rounded-none flex items-center justify-center mb-6">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2z" /></svg>
                    </div>
                    <p className="uppercase text-[12px] font-black tracking-widest">Select evidence or <span className="text-[#0000a9]">drag and drop</span> to begin neural calibration.</p>
                  </div>
                )}
                {isDraggingOverLab && (
                  <div className="absolute inset-0 bg-[#0000a9]/20 backdrop-blur-sm flex items-center justify-center z-50 pointer-events-none">
                    <div className="bg-slate-900 p-8 rounded-none border-2 border-[#0000a9] shadow-2xl text-white font-black uppercase tracking-widest">Release to Import Evidence</div>
                  </div>
                )}
              </div>
              
              {/* LEGEND ON LEFT SIDE */}
              <div className="p-6 bg-slate-500/10 border-t border-slate-500/30 flex gap-8 items-start shadow-inner shrink-0">
                <div className="flex-1 space-y-3">
                  <h6 className="text-[10px] font-black text-slate-900 uppercase tracking-[0.2em] italic flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#0000a9]" />
                    Neural Classification Guide
                  </h6>
                  <div className="grid grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-3">
                    {defectTypes.slice(0, 8).map(t => (
                      <div key={t.type} className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                        <div className="w-3 h-3 shrink-0 shadow-sm border border-slate-400/50" style={{ backgroundColor: t.hex }} />
                        <span className="text-[10px] font-black text-slate-800 uppercase truncate">{t.type}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="w-px self-stretch bg-slate-500/20 mx-2" />
                <div className="min-w-[200px] xl:min-w-[300px] space-y-3">
                  <h6 className="text-[10px] font-black text-slate-900 uppercase tracking-[0.2em] italic flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#0000a9]" />
                    Severity Status
                  </h6>
                  <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                    {Object.entries(Severity).map(([key, val]) => (
                      val !== Severity.NONE && (
                        <div key={val} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full shadow-sm border border-slate-400/50" style={{ backgroundColor: { [Severity.LOW]: '#3b82f6', [Severity.MEDIUM]: '#eab308', [Severity.HIGH]: '#f97316', [Severity.CRITICAL]: '#ef4444' }[val as any] }} />
                          <span className="text-[10px] font-black text-slate-800 uppercase">{val}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </div>

            </div>

            <div className="lg:col-span-4 bg-slate-400 border border-slate-500 rounded-none flex flex-col shadow-xl overflow-hidden">
              <div className="flex flex-col h-full animate-fadeIn">
                <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col">
                  {selectedFindingIdx !== null && findings[selectedFindingIdx] ? (
                    <div className="flex flex-col flex-shrink-0">
                      <div className="p-8 border-b border-slate-500/30 bg-slate-500/20 flex justify-between items-center">
                        <div>
                          <h4 className="text-slate-900 font-black uppercase text-sm italic leading-none">Baseline Calibration</h4>
                          <p className="text-[10px] text-[#0000a9] font-black mt-2 uppercase tracking-widest">DEFINING GROUND TRUTH {findings[selectedFindingIdx].labId ?? `EV_V6.0_${selectedFindingIdx + 1}`}</p>
                        </div>
                      </div>
                      <div className="p-8 space-y-6">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-[9px] font-black text-slate-800 uppercase tracking-widest">Classification</label>
                            <button
                              onClick={() => setShowCustomTypeForm(true)}
                              className="text-[8px] font-black text-[#0000a9] uppercase tracking-widest hover:text-white transition"
                            >
                              + Create Custom Category
                            </button>
                          </div>
                          <div className="flex flex-col gap-3">
                            <select
                              value={findings[selectedFindingIdx].type}
                              onChange={(e) => {
                                const newType = e.target.value;
                                const dType = defectTypes.find(dt => dt.type === newType);
                                updateFinding(selectedFindingIdx!, {
                                  type: newType,
                                  description: dType ? dType.desc : findings[selectedFindingIdx].description
                                });

                              }}
                              className="w-full bg-slate-300 border border-slate-500 rounded-none p-4 text-[12px] text-slate-900 uppercase font-black outline-none focus:border-[#0000a9]"
                            >
                              {defectTypes.map(t => <option key={t.type} value={t.type}>{t.title}</option>)}
                            </select>
                            {(missionFindings.length > 0 || (initialLabData?.allFindings && initialLabData.allFindings.length > 0)) ? (
                              <div className="flex items-stretch gap-2">
                                <button
                                  onClick={() => navigateToMissionFinding(Math.max(0, (currentMissionIdx ?? 0) - 1))}
                                  disabled={(currentMissionIdx ?? 0) === 0 || missionFindings.length === 0}
                                  className="flex-1 py-4 flex items-center justify-center gap-3 bg-[#0000a9] text-white border border-[#ffffff]/20 rounded-none text-[10px] font-black uppercase tracking-[0.2em] transition-all disabled:opacity-30 hover:bg-[#0000c0] shadow-xl"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                                  Prev
                                </button>
                                <div className="px-4 py-4 bg-slate-400/50 border border-slate-500 text-[11px] font-black text-[#0000a9] uppercase tracking-[0.1em] text-center min-w-[80px] shadow-inner flex items-center justify-center">
                                  {missionFindings.length > 0 ? (currentMissionIdx ?? 0) + 1 : 0} / {missionFindings.length}
                                </div>
                                <button
                                  onClick={() => navigateToMissionFinding(Math.min(missionFindings.length - 1, (currentMissionIdx ?? 0) + 1))}
                                  disabled={missionFindings.length <= 1 || (currentMissionIdx ?? 0) === missionFindings.length - 1}
                                  className="flex-1 py-4 flex items-center justify-center gap-3 bg-[#0000a9] text-white border border-[#ffffff]/20 rounded-none text-[10px] font-black uppercase tracking-[0.2em] transition-all disabled:opacity-30 hover:bg-[#0000c0] shadow-xl"
                                >
                                  Next
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                </button>
                                <button
                                  onClick={() => {
                                    updateFinding(selectedFindingIdx!, { isVerified: true });
                                    // Auto-commit to vault on OK
                                    saveToVault(false);

                                    // Visual feedback for accepting the baseline
                                    const btn = document.getElementById('accept-baseline-btn');
                                    if (btn) {
                                      btn.innerText = 'ACCEPTED';
                                      btn.classList.add('bg-emerald-500', 'text-slate-950');

                                      // AUTO-JUMP to next anomaly after brief confirmation period
                                      setTimeout(() => {
                                        if (btn) {
                                          btn.innerText = 'OK';
                                          btn.classList.remove('bg-emerald-500', 'text-slate-950');
                                        }

                                        // Navigate to next mission finding if available
                                        if (currentMissionIdx !== null && currentMissionIdx < missionFindings.length - 1) {
                                          navigateToMissionFinding(currentMissionIdx + 1);
                                        }
                                      }, 600);
                                    }
                                  }}
                                  id="accept-baseline-btn"
                                  className={`px-4 min-w-[120px] font-black uppercase text-[10px] tracking-widest shadow-lg transition-all active:scale-95 border ${findings[selectedFindingIdx].isVerified ? 'bg-emerald-600 text-white border-emerald-500' : 'bg-slate-700 hover:bg-[#0000a9] text-white border-slate-600'}`}
                                >
                                  {findings[selectedFindingIdx].isVerified ? 'ACCEPTED' : 'OK'}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  updateFinding(selectedFindingIdx!, { isVerified: true });
                                  // Auto-commit to vault on OK
                                  saveToVault(false);

                                  // Visual feedback for accepting the baseline
                                  const btn = document.getElementById('accept-baseline-btn');
                                  if (btn) {
                                    btn.innerText = 'ACCEPTED';
                                    btn.classList.add('bg-emerald-500', 'text-slate-950');

                                    // AUTO-JUMP to next anomaly after brief confirmation period
                                    setTimeout(() => {
                                      if (btn) {
                                        btn.innerText = 'OK';
                                        btn.classList.remove('bg-emerald-500', 'text-slate-950');
                                      }

                                      // Navigate to next mission finding if available
                                      if (currentMissionIdx !== null && currentMissionIdx < missionFindings.length - 1) {
                                        navigateToMissionFinding(currentMissionIdx + 1);
                                      }
                                    }, 600);
                                  }
                                }}
                                id="accept-baseline-btn"
                                className={`w-full py-4 font-black uppercase text-[10px] tracking-widest shadow-lg transition-all active:scale-95 border ${findings[selectedFindingIdx].isVerified ? 'bg-emerald-600 text-white border-emerald-500' : 'bg-slate-700 hover:bg-[#0000a9] text-white border-slate-600'}`}
                              >
                                {findings[selectedFindingIdx].isVerified ? 'ACCEPTED' : 'OK'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-3 pt-2">
                          <label className="text-[9px] font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                            Adjust Criticality
                            <span className="text-[8px] opacity-40">(Priority Level)</span>
                          </label>
                          <div className="flex gap-1.5">
                            {Object.values(Severity).map(s => {
                              if (s === Severity.NONE) return null; // Exclude NONE from buttons
                              const isActive = findings[selectedFindingIdx].severity === s;
                              const sColor = {
                                [Severity.NONE]: '#64748b', // Should not be reached
                                [Severity.LOW]: '#3b82f6',
                                [Severity.MEDIUM]: '#eab308',
                                [Severity.HIGH]: '#f97316',
                                [Severity.CRITICAL]: '#ef4444',
                              }[s] || '#64748b';

                              return (
                                <button
                                  key={s}
                                  onClick={() => updateFinding(selectedFindingIdx!, { severity: s })}
                                  className={`flex-1 py-3 text-[9px] font-black uppercase transition-all border ${isActive ? 'text-white border-transparent scale-[1.05] z-10 shadow-xl' : 'bg-slate-300 text-slate-600 border-slate-400 hover:border-slate-500'}`}
                                  style={{ backgroundColor: isActive ? sColor : undefined }}
                                >
                                  {s}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-[9px] font-black text-slate-800 uppercase tracking-widest">Defect Detail Description</label>

                          <textarea value={findings[selectedFindingIdx].description} onChange={(e) => updateFinding(selectedFindingIdx!, { description: e.target.value })} className="w-full bg-slate-300 border border-slate-500 rounded-none p-4 text-[12px] text-slate-900 h-32 resize-none outline-none focus:border-[#0000a9] font-black uppercase" />
                        </div>
                        <button
                          onClick={() => {
                            const deletedIdx = selectedFindingIdx!;
                            // Compute fallback within the current frame: previous finding (or 0 if at start).
                            const nextWithinFrame = deletedIdx > 0 ? deletedIdx - 1 : findings.length > 1 ? 0 : null;
                            removeFinding(deletedIdx);
                            if (nextWithinFrame !== null) {
                              // Stay on current frame, just select a sibling.
                              setSelectedFindingIdx(nextWithinFrame);
                            } else if (currentMissionIdx !== null && currentMissionIdx < missionFindings.length - 1) {
                              // Current frame now empty — auto-advance to the next mission finding.
                              navigateToMissionFinding(currentMissionIdx + 1);
                            } else {
                              // End of the mission and nothing else to jump to.
                              setSelectedFindingIdx(null);
                            }
                          }}
                          className="w-full py-3 bg-red-950/20 text-red-500 border border-red-900/30 rounded-none text-[9px] font-black uppercase tracking-widest hover:bg-red-950/40 transition-all flex items-center justify-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          Delete Anomaly
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center p-12 text-center opacity-40">
                      <h4 className="text-slate-900 font-black uppercase text-sm tracking-widest mb-2">Select Anomaly</h4>
                      <p className="text-[10px] uppercase font-bold text-slate-800">Touch a box to adjust parameters.</p>
                    </div>
                  )}

                  <div className="flex flex-col flex-shrink-0 mt-auto">

                    <div className="p-6 border-t border-slate-500/30 bg-slate-500/10 flex flex-col gap-4">
                      {/* REPORT GENERATION BLOCK */}
                      <div className="flex flex-col gap-2.5">
                        {/* PRIMARY CUSTOMER REPORT */}
                        <button
                          onClick={async () => {
                            const allVerified = missionFindings.filter(f => f.isVerified);
                            if (allVerified.length === 0) {
                              alert("No anomalies have been 'OK'd' yet. Please verify findings before generating the technical report.");
                              return;
                            }

                            const customerName = initialLabData?.customer || 'GEA';
                            const assetName = initialLabData?.asset || 'Asset';
                            const reportDate = new Date().toLocaleDateString();
                            const mockAnalysis: CrackAnalysis = {
                              detected: true,
                              findings: allVerified,
                              severity: allVerified.reduce((max, f) => {
                                const rank: Record<string, number> = { NONE: 0, LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 };
                                return rank[f.severity] > rank[max] ? f.severity : max;
                              }, 'NONE' as any),
                              description: `${customerName} Technical Audit — ${assetName} — ${reportDate}: ${allVerified.length} verified defects identified and baseline calibrated.`,
                              recommendation: "Refer to the verified technical baselines and remediation paths in this report.",
                              diagnosticNote: "Refined Laboratory Audit | Source of Truth V6.0",
                              confidenceScore: 1.0,
                              timestamp: Date.now() / 1000,
                              mode: InspectionMode.GENERAL_NDT,
                              videoName: `${customerName} Technical Report — ${assetName}`
                            };
                            const cleanTimeline = initialLabData?.timeline?.map(t => ({ ...t, findings: [] }));
                            await generateInspectionReport(mockAnalysis, labImage || (allVerified[0] as any).frameImage, cleanTimeline, `${customerName.replace(/\s+/g, '_')}_technical_report_`, undefined, 'CUSTOMER');
                          }}
                          className="w-full py-5 bg-[#0000a9] hover:bg-[#0000c0] text-white rounded-none text-[10px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 shadow-2xl border border-white/20"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          Generate GEA Technical ( Customer ) Report
                        </button>

                        {/* REPAIR REPORT — CONDITIONAL ON RADIALLY SPATIAL MISSION DATA */}
                        {initialLabData?.hasTelemetry && (
                          <button
                            onClick={async () => {
                              const allVerified = missionFindings.filter(f => f.isVerified);
                              if (allVerified.length === 0) {
                                alert("No anomalies have been 'OK'd' yet. Please verify findings before generating the repair report.");
                                return;
                              }

                              const customerName = initialLabData?.customer || 'GEA';
                              const assetName = initialLabData?.asset || 'Asset';
                              const reportDate = new Date().toLocaleDateString();
                              const mockAnalysis: CrackAnalysis = {
                                detected: true,
                                findings: allVerified,
                                severity: allVerified.reduce((max, f) => {
                                  const rank: Record<string, number> = { NONE: 0, LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 };
                                  return rank[f.severity] > rank[max] ? f.severity : max;
                                }, 'NONE' as any),
                                description: `${customerName} Repair Guidance — ${assetName} — ${reportDate}: ${allVerified.length} verified defects identified for remediation.`,
                                recommendation: "Execute repairs according to GEA Engineering standards and localized spatial data provided in this guide.",
                                diagnosticNote: "Localized Repair Strategy | NDT-PRO V6.0",
                                confidenceScore: 1.0,
                                timestamp: Date.now() / 1000,
                                mode: InspectionMode.GENERAL_NDT,
                                videoName: `${customerName} Repair Report — ${assetName}`
                              };
                              const cleanTimeline = initialLabData.timeline.map(t => ({ ...t, findings: [] }));
                              await generateInspectionReport(mockAnalysis, labImage || (allVerified[0] as any).frameImage, cleanTimeline, `${customerName.replace(/\s+/g, '_')}_repair_report_`, undefined, 'REPAIR');
                            }}
                            className="w-full py-4 bg-slate-800 hover:bg-slate-700 text-white rounded-none text-[9px] font-black uppercase tracking-[0.15em] transition-all flex items-center justify-center gap-2.5 border border-slate-600 shadow-xl"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            Generate GEA Repair Report
                          </button>
                        )}

                        {/* JUMP TO 3D MAP — CONDITIONAL ON RADIALLY SPATIAL MISSION DATA */}
                        {initialLabData?.hasTelemetry && (
                          <button
                            onClick={() => onViewTrajectory?.(missionFindings)}
                            className="w-full py-4 bg-cyan-950/40 hover:bg-cyan-900/60 text-cyan-400 rounded-none text-[9px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2.5 border border-cyan-800/50 shadow-xl"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A2 2 0 013 15.487V6.513a2 2 0 011.553-1.943L9 2l5.447 2.724A2 2 0 0116 6.513v8.974a2 2 0 01-1.553 1.943L9 20zm0-13v13m0-13l5 2.5m-5 10.5l5-2.5" /></svg>
                            3D Spatial Review
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'INSTRUCTOR' && (
          <div className="h-full flex flex-col gap-8">
            <div className="flex gap-4">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Query technical metallurgy..." className="flex-1 bg-slate-900 border border-slate-800 rounded-none px-8 py-5 text-white outline-none focus:border-[#0000a9]" />
              <button onClick={askInstructor} className="bg-[#0000a9] text-white px-12 rounded-none font-black uppercase text-[11px] tracking-widest transition-all hover:bg-[#0000c0]">Search</button>
            </div>
            {response && <div className="flex-1 bg-slate-300 border border-slate-400 rounded-none p-10 overflow-y-auto shadow-xl text-slate-900 leading-relaxed">{response}</div>}
          </div>
        )}

        {activeTab === 'LIBRARY' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 overflow-y-auto pb-12">
            {defectTypes.map((d, i) => (
              <div key={i} className="bg-slate-300 border border-slate-400 p-8 rounded-none shadow-sm hover:border-[#0000a9]/30 transition-all">
                <span className="text-3xl mb-4 block">{d.icon}</span>
                <h3 className={`text-sm font-black uppercase tracking-widest mb-2 ${d.color || 'text-slate-900'}`}>{d.title}</h3>
                <p className="text-[11px] text-slate-800 italic">{d.desc}</p>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Custom Category Modal */}
      {showCustomTypeForm && (
        <div className="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-6 animate-fadeIn">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-none shadow-2xl overflow-hidden">
            <div className="p-8 border-b border-slate-800 bg-slate-800/30 flex justify-between items-center">
              <div>
                <h3 className="text-white font-black uppercase tracking-widest text-sm italic">New Classification</h3>
                <p className="text-[9px] text-slate-800 font-mono mt-1 uppercase tracking-widest">Define custom anomaly profile</p>
              </div>
              <button onClick={() => setShowCustomTypeForm(false)} className="text-slate-800 hover:text-white transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleAddCustomType} className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-800 uppercase tracking-widest ml-1">Title</label>
                <input
                  type="text"
                  value={customTypeData.title}
                  onChange={(e) => setCustomTypeData({ ...customTypeData, title: e.target.value })}
                  placeholder="e.g. Surface Etching"
                  className="w-full bg-slate-950 border border-slate-700 rounded-none p-4 text-white text-sm outline-none focus:border-[#0000a9] transition"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-800 uppercase tracking-widest ml-1">Icon (Emoji)</label>
                <input
                  type="text"
                  value={customTypeData.icon}
                  onChange={(e) => setCustomTypeData({ ...customTypeData, icon: e.target.value })}
                  placeholder="🏷️"
                  className="w-full bg-slate-950 border border-slate-700 rounded-none p-4 text-white text-sm outline-none focus:border-[#0000a9] transition"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-800 uppercase tracking-widest ml-1">Description</label>
                <textarea
                  value={customTypeData.desc}
                  onChange={(e) => setCustomTypeData({ ...customTypeData, desc: e.target.value })}
                  placeholder="Describe visual characteristics..."
                  className="w-full bg-slate-950 border border-slate-700 rounded-none p-4 text-white text-sm outline-none focus:border-[#0000a9] transition h-24 resize-none"
                />
              </div>
              <button type="submit" className="w-full py-5 bg-[#0000a9] hover:bg-[#0000c0] text-white rounded-none font-black uppercase text-[10px] tracking-widest transition-all shadow-xl active:scale-95">
                Register Classification
              </button>
            </form>
          </div>
        </div>
      )}
      {isSaving && <LoadingOverlay message="COMMITTING GROUND TRUTH" subMessage="SYNCHRONIZING SECURE REPOSITORY TO PROJECT STORAGE" />}

      {vaultEditEntry && (
        <VaultEntryEditor
          entry={vaultEditEntry}
          onClose={() => setVaultEditEntry(null)}
          onSaved={async () => {
            // Reload vault after save — picks up the new (replaced) entry
            try { const fresh = await getVaultEntries(); setGroundTruthEntries(fresh); } catch (e) { console.warn('Vault reload failed:', e); }
          }}
          onDeleted={async () => {
            try { const fresh = await getVaultEntries(); setGroundTruthEntries(fresh); } catch (e) { console.warn('Vault reload failed:', e); }
          }}
        />
      )}
    </div>
  );
};
